import {
  DefaultSiteViewContextProvider,
  useGetUnitDisplayPropertiesFn,
} from 'features/overview'
import { useRolodexEntries } from 'features/rolodex'
import { SiteViewMap } from 'features/siteMap'
import React from 'react'
import styled from 'styled-components'
import { GeckoGeographicPin, GeckoSite, RolodexEntryType } from 'types'

const SiteViewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

interface WalkdownSiteViewProps {
  site: GeckoSite
  selectedUnitIds?: string[]
  walkdownId: string
}

export const WalkdownSiteView: React.FC<WalkdownSiteViewProps> = ({
  site,
  selectedUnitIds = [],
  walkdownId,
}) => {
  const getUnitDisplayProperties = useGetUnitDisplayPropertiesFn(
    [],
    false,
    new Date().getUTCFullYear()
  )

  // Fetch walkdown unit links
  const { entries: unitLinks } = useRolodexEntries({
    type: [RolodexEntryType.WALKDOWN_UNIT_LINK],
    tags: {
      [RolodexEntryType.WALKDOWN]: walkdownId,
    },
    links: [RolodexEntryType.UNIT],
  })

  // Fetch walkdown report links
  const { reports: reportLinks } = useRolodexEntries({
    type: [RolodexEntryType.WALKDOWN_REPORT_LINK],
    tags: {
      [RolodexEntryType.WALKDOWN]: walkdownId,
    },
    links: [RolodexEntryType.REPORT],
  })

  // Get all unit IDs from the walkdown
  const walkdownUnitIds = React.useMemo(
    () =>
      unitLinks
        .map((link) => link.tags.unit)
        .filter(
          (unitId): unitId is string => unitId !== null && unitId !== undefined
        ),
    [unitLinks]
  )

  // Get all report IDs from the walkdown
  const walkdownReportIds = React.useMemo(
    () => reportLinks.map((report) => report.id),
    [reportLinks]
  )

  // Filter pins based on selected units
  const shouldDisplayPin = React.useCallback(
    async (pin: GeckoGeographicPin) => {
      const pinUnitId = pin.tags[RolodexEntryType.UNIT]
      const pinReportId = pin.tags[RolodexEntryType.REPORT]

      // If no units are selected, show all walkdown pins
      if (selectedUnitIds.length === 0) {
        return (
          walkdownUnitIds.includes(pinUnitId ?? '') ||
          walkdownReportIds.includes(pinReportId ?? '')
        )
      }

      // If units are selected, only show pins for those units
      return selectedUnitIds.includes(pinUnitId ?? '')
    },
    [selectedUnitIds, walkdownUnitIds, walkdownReportIds]
  )

  return (
    <SiteViewContainer>
      <DefaultSiteViewContextProvider currentSite={site}>
        <SiteViewMap
          site={site}
          getUnitDisplayProperties={getUnitDisplayProperties}
          componentToDisplayOnFeatureClick={undefined}
          mapInteractionAPIs={{
            shouldDisplayTaggedUnit: () => true,
            getExtraLabelDisplay: () => null,
            shouldDisplayPin,
          }}
        />
      </DefaultSiteViewContextProvider>
    </SiteViewContainer>
  )
}
